/**
 * TTDSG- und DSGVO-konformer Cookie-/Analytics-Einwilligungsbanner.
 * PostHog-Start: nach Login via PostHogPageView; bei „Akzeptieren“ hier applyPostHogConsent (kein SDK auf öffentlichen Seiten vor Login).
 * - Keine vorangekreuzten Kästchen (aktive, eindeutige Einwilligung).
 * - Klare Sprache; getrennte Buttons Akzeptieren / Ablehnen; Speicherung nur nach Klick.
 */

import { useCallback } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { setStoredAnalyticsConsent, type AnalyticsConsentStatus } from "@/lib/analytics-consent";
import { applyPostHogConsent } from "@/lib/posthog";
import { useAuth } from "@/contexts/useAuth";
import { toSafeReactNode } from "@/lib/utils";
import { apiPatch } from "@/api";

const CONSENT_BANNER_ID = "analytics-consent-banner";

type Props = {
  onConsentChange?: (status: AnalyticsConsentStatus) => void;
};

export function AnalyticsConsentBanner({ onConsentChange }: Props) {
  const { t } = useTranslation("common");
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;

  const handleAccept = useCallback(() => {
    setStoredAnalyticsConsent("accepted");
    /** Nur eingeloggt: SDK laden (Gäste: erst nach Login via PostHogPageView — kein PostHog auf öffentlichen Routen). */
    if (state.status === "authenticated") {
      applyPostHogConsent("accepted");
    }
    if (token) {
      apiPatch("/me", token, { analytics_consent_accepted: true }).catch(() => {});
    }
    onConsentChange?.("accepted");
  }, [onConsentChange, token, state.status]);

  const handleReject = useCallback(() => {
    setStoredAnalyticsConsent("rejected");
    if (token) {
      apiPatch("/me", token, { analytics_consent_accepted: false }).catch(() => {});
    }
    applyPostHogConsent("rejected");
    onConsentChange?.("rejected");
  }, [onConsentChange, token]);

  return (
    <div
      id={CONSENT_BANNER_ID}
      role="dialog"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-desc"
      className="fixed bottom-0 left-0 right-0 z-[9999] border border-border bg-surface p-4 shadow-2xl sm:left-4 sm:right-4 sm:bottom-4 sm:rounded-2xl sm:max-w-lg sm:border-accent/20"
    >
      <h2 id="consent-banner-title" className="text-base font-semibold text-text mb-1">
        {toSafeReactNode(t("consent.title"))}
      </h2>
      <p id="consent-banner-desc" className="text-sm text-text-muted mb-4">
        {toSafeReactNode(t("consent.description"))}{" "}
        <a
          href="/privacy"
          className="text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded font-medium"
        >
          {toSafeReactNode(t("consent.privacyLink"))}
        </a>
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleAccept}
          className="inline-flex items-center justify-center rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg transition-opacity"
        >
          {toSafeReactNode(t("consent.accept"))}
        </button>
        <button
          type="button"
          onClick={handleReject}
          className="inline-flex items-center justify-center rounded-lg border border-border bg-surface-2 px-5 py-2.5 text-sm font-medium text-text-muted hover:text-text hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg transition-colors"
        >
          {toSafeReactNode(t("consent.reject"))}
        </button>
      </div>
    </div>
  );
}
